import React from 'react';
import styled from 'styled-components';
import {Header, flexRender} from '@tanstack/react-table';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Icon, tokensForThemeProvider} from '@forecast-it/design-system';
import {ReportRow} from '../data/reportRowModel';
import {FIXED_COLUMN_IDS} from './useReportTableColumns';

const tokens = tokensForThemeProvider.sky.sys;

/** the design's header row trailing sort affordance (`Table/Column/.Cell/Right Icon/.icon-sort`)
 * is a single combined sort glyph, not a stacked chevron pair — the DS icon
 * set does export a matching single `sort` icon (`icons.d.ts`). Per the design, the
 * `.icon-sort` glyph is the default dark on-light colour (#232334) even when the column is
 * unsorted, not a muted/disabled grey; once actively sorted, the direction chevron
 * (`chevronUp`/`chevronDown`) replaces it. The DS `newColor="onlight"` icon token resolves to
 * exactly #232334 (icon.tokens `newColor.onlight`), whereas `color="default"` is #393946. */
const SortIconWrapper = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	/* the design: .Cell itemSpacing between the header label and the sort glyph is 8px. */
	margin-left: ${tokens.spacing['200']};
`;

/* Label + sort glyph share one horizontal axis, vertically centered against each other (the design
 * : the header Text and .icon-sort sit at the same y, both centered in the cell). An
 * inline-flow th left the inline-flex SortIconWrapper on the text baseline, so the sort icon read
 * as vertically off. A flex row with align-items:center centers them together. */
const HeaderContent = styled.span`
	display: inline-flex;
	align-items: center;
`;

const HeaderCell = styled.th<{$sortable: boolean}>`
	position: relative;
	text-align: left;
	padding: ${tokens.spacing['450']} ${tokens.spacing['400']};
	/* the design's column header is ABC Whyte 14 / weight 500 (not 600). */
	font-weight: 500;
	color: ${tokens.color.text.content.main};
	cursor: ${({$sortable}) => ($sortable ? 'pointer' : 'default')};
	user-select: none;
	white-space: nowrap;
`;

const ResizeHandle = styled.div<{$isResizing: boolean}>`
	position: absolute;
	top: 0;
	right: 0;
	height: 100%;
	width: 6px;
	cursor: col-resize;
	user-select: none;
	touch-action: none;
	background: ${({$isResizing}) => ($isResizing ? tokens.color.border.enabled : 'transparent')};
`;

export interface DraggableHeaderProps {
	header: Header<ReportRow, unknown>;
}

/** A single header cell wired for both drag-reorder (dnd-kit sortable) and column resize
 * (TanStack `columnResizeMode: 'onChange'`). Fixed columns (`__expander`/`__actions`) are not
 * draggable and never render a resize handle. */
export function DraggableHeader({header}: DraggableHeaderProps): JSX.Element {
	const columnId = header.column.id;
	const isFixed = FIXED_COLUMN_IDS.includes(columnId);
	const sortable = header.column.getCanSort();
	const canResize = header.column.getCanResize() && !isFixed;

	const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
		id: columnId,
		disabled: isFixed,
	});

	const style: React.CSSProperties = {
		width: header.getSize(),
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	const dragProps = isFixed ? {} : {...attributes, ...listeners};

	return (
		<HeaderCell
			ref={setNodeRef}
			$sortable={sortable}
			style={style}
			onClick={sortable ? header.column.getToggleSortingHandler() : undefined}
			{...dragProps}
		>
			{header.isPlaceholder ? null : (
				<HeaderContent>
					{flexRender(header.column.columnDef.header, header.getContext())}
					{sortable ? (
						<SortIconWrapper data-testid={`sort-icon-${columnId}`}>
							{header.column.getIsSorted() ? (
								<Icon
									icon={header.column.getIsSorted() === 'asc' ? 'chevronUp' : 'chevronDown'}
									size={'s'}
									color={'default'}
								/>
							) : (
								<Icon icon={'sort'} size={'s'} newColor={'onlight'} />
							)}
						</SortIconWrapper>
					) : null}
				</HeaderContent>
			)}
			{canResize && (
				<ResizeHandle
					$isResizing={header.column.getIsResizing()}
					onMouseDown={header.getResizeHandler()}
					onTouchStart={header.getResizeHandler()}
					data-testid={`resize-handle-${columnId}`}
				/>
			)}
		</HeaderCell>
	);
}
